import { Button } from '@/components/Button';
import { Text } from '@/components/Text';
import { TextInput } from '@/components/TextInput';
import { useSubscription } from '@/hooks/useSubscription';
import { IconEmojiPicker } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/IconEmojiPicker';
import { LinkIcon } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/LinkIcon';
import { RenderLinkFields } from '@/partials/dashboard/Cards/UpdateCard/UpdateCardLinks/RenderLinkFields';
import { useCardStore } from '@/stores/useCardStore';
import { CardLink } from '@/types/card';
import { getUrlForLink } from '@/utils/getUrlForLink';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiExternalLink } from 'react-icons/fi';
import { IoArrowBack } from 'react-icons/io5';
import { MdOutlineDelete } from 'react-icons/md';
import * as Yup from 'yup';

interface UpdateCardLinkProps {
  link: CardLink;
  onClose: () => void;
}

export const UpdateCardLink = ({ link, onClose }: UpdateCardLinkProps) => {
  const { t } = useTranslation();
  const { card, updateCardLink, deleteCardLink } = useCardStore();
  const { hasActiveSubscription } = useSubscription(card?.user || null);
  const [linkData, setLinkData] = useState<CardLink | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const LinkSchema = Yup.object().shape({
    title: Yup.string().required(t('common:validation.required')),
    url: Yup.string()
      .required(t('common:validation.required'))
      .test(
        'phone-validation',
        t('common:validation.phone.format'),
        function (value: string | undefined) {
          const { icon } = this.parent;
          if (['call', 'message'].includes(icon?.toLowerCase())) {
            // Check if the value is more than just a country code prefix (typically +XX)
            return Boolean(
              value && value.replace(/^\+\d{1,4}/, '').trim().length > 0,
            );
          }
          return true;
        },
      ),
  });

  useEffect(() => {
    if (!link) return;
    setLinkData(link);
  }, [link]);

  const handleSubmit = async (
    values: CardLink,
    { setErrors }: FormikHelpers<CardLink>,
  ) => {
    setIsUpdating(true);
    if (!linkData) return;
    try {
      await updateCardLink(linkData?.id, {
        ...linkData,
        ...values,
      });
      setLinkData(null);
      onClose();
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleTestLink = () => {
    if (linkData && linkData.url) {
      const formattedUrl = getUrlForLink(linkData.icon, linkData.url);
      window.open(formattedUrl, '_blank');
    }
  };

  const handleRemove = async () => {
    if (!card || !linkData) return;

    setIsUpdating(true);
    await deleteCardLink(linkData.id);
    onClose();
    setIsUpdating(false);
  };

  if (!linkData) return null;

  return (
    <div className="flex flex-col space-y-4">
      {/* Header with back button */}
      <div className="mt-2 flex items-center">
        <button
          type="button"
          onClick={onClose}
          className="mr-2 bg-transparent p-0 text-gray-600 shadow-none hover:text-gray-900"
        >
          <IoArrowBack size={24} />
        </button>
        <Text className=" text-xl font-medium">
          {t('updateCard:links.edit_link')}
        </Text>
      </div>

      <Formik
        initialValues={linkData}
        validationSchema={LinkSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty }) => (
          <Form className="flex flex-col space-y-4">
            {link.type === 'custom_link' ? (
              <IconEmojiPicker />
            ) : (
              <LinkIcon
                link={link}
                className="size-16"
                iconStyle="size-14 rounded-[14px] aspect-square shadow-sm"
                emojiStyle="text-4xl size-14 rounded-[14px] aspect-square"
              />
            )}

            <div className="space-y-4 bg-transparent">
              <TextInput
                name="title"
                disabled={!hasActiveSubscription()}
                type="text"
                label={t('updateCard:links.link_title')}
                placeholder={t(`icon:${linkData.icon}_placeholder`)}
              />

              <RenderLinkFields
                icon={linkData.icon}
                type={linkData.type}
                t={t}
              />
              <Button
                type="button"
                onClick={handleTestLink}
                className="gap-2 bg-transparent p-0 text-linko-light shadow-none hover:shadow-none"
              >
                <FiExternalLink size={16} />
                {t('updateCard:links.test_link')}
              </Button>
            </div>

            <div className="flex flex-col-reverse gap-4">
              <Button
                type="button"
                onClick={handleRemove}
                className="gap-2 bg-transparent p-0 text-danger shadow-none hover:bg-transparent hover:shadow-none"
                disabled={isUpdating}
              >
                <MdOutlineDelete size={18} />
                {t('updateCard:links.remove')}
              </Button>

              <div className="flex gap-2">
                <Button type="button" onClick={onClose} disabled={isUpdating}>
                  {t('common:cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={!isValid || !dirty || isUpdating}
                  loading={isUpdating}
                >
                  {t('common:update')}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
